import { t } from "@/i18n";
import { Lock } from "lucide-react";
import { MILESTONES, STICKERS } from "@/lib/stickers";

const Badge = ({ m, got }) => {
  const Icon = m.icon;
  return (
    <div data-testid={`milestone-${m.id}`} data-earned={got} title={got ? m.text : `Collect ${m.at} stickers to unlock`}
      className={`flex items-center gap-3 rounded-2xl border-[3px] px-4 py-3 ${got ? "border-white shadow-[0_0_0_3px_#1E293B]" : "border-dashed border-slate-300 bg-white/60"}`}
      style={{ backgroundColor: got ? m.bg : undefined }}>
      <Icon className={`w-9 h-9 ${got ? "text-[#1E293B]" : "text-slate-300"}`} strokeWidth={2.2} fill={got ? "white" : "none"} />
      <div className="min-w-0">
        <p className={`font-display font-bold leading-tight ${got ? "text-[#1E293B]" : "text-slate-400"}`}>{m.text}</p>
        <p className={`text-xs font-bold ${got ? "text-[#1E293B]/70" : "text-slate-400"}`}>{got ? m.blurb : `${m.at} stickers`}</p>
      </div>
      {!got && <Lock className="w-4 h-4 text-slate-300 ml-auto shrink-0" />}
    </div>
  );
};

export const StickerShelf = ({ earned = [], badges = [] }) => {
  const have = new Set(earned);
  const pct = Math.round((have.size / STICKERS.length) * 100);
  return (
    <section className="card-soft p-6 sm:p-8 space-y-6" data-testid="sticker-shelf">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold">{t("my_sticker_shelf")}</h2>
          <p className="text-slate-600">{t("save_a_coloured_picture_to_earn_a_new_st")}</p>
        </div>
        <p className="font-display text-xl font-bold" data-testid="sticker-shelf-count">{have.size} / {STICKERS.length} collected</p>
      </div>
      <div className="h-4 rounded-full bg-[#F3EFE6] border-2 border-slate-200 overflow-hidden relative">
        <div className="h-full bg-[#FF5252] transition-[width] duration-700" style={{ width: `${pct}%` }} data-testid="sticker-shelf-progress" />
        <span className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-[#1E293B]/30" />
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-3 sm:gap-4">
        {STICKERS.map((s, i) => {
          const Icon = s.icon;
          const got = have.has(s.id);
          return (
            <div key={s.id} title={got ? s.text : "Not collected yet"} data-testid={`sticker-slot-${s.id}`} data-earned={got}
              className={`aspect-square rounded-3xl flex flex-col items-center justify-center gap-1 border-[3px] transition-transform duration-200 ${
                got ? "border-white shadow-[0_0_0_3px_#1E293B] hover:-translate-y-1 hover:rotate-3 sticker-pop" : "border-dashed border-slate-300 bg-white/60"
              }`}
              style={{ backgroundColor: got ? s.bg : undefined, animationDelay: `${i * 40}ms` }}>
              {got ? (
                <>
                  <Icon className="w-8 h-8 sm:w-9 sm:h-9 text-[#1E293B]" strokeWidth={2.2} fill="white" />
                  <span className="text-[10px] font-bold text-[#1E293B] text-center leading-tight px-1 hidden sm:block">{s.text}</span>
                </>
              ) : (
                <>
                  <Icon className="w-8 h-8 sm:w-9 sm:h-9 text-slate-300" strokeWidth={1.8} />
                  <Lock className="w-3 h-3 text-slate-300" />
                </>
              )}
            </div>
          );
        })}
      </div>
      <div className="pt-2">
        <p className="text-sm font-bold text-slate-700 mb-3">{t("bonus_badges")}</p>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="milestone-badges">
          {MILESTONES.map((m) => <Badge key={m.id} m={m} got={badges.includes(m.id)} />)}
        </div>
      </div>
    </section>
  );
};
